import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, AdminError } from "../adminClient.js";
import { badgeClass } from "../badge.js";

/** Mirrors the server `buildBatchReport` output (services/lambdas/src/lib/batchReport.ts). */
type Flag =
  | { type: "fail"; quiz: string; score: number }
  | { type: "homework"; week: string }
  | { type: "owes"; amount: number }
  | { type: "paid" }
  | { type: "toreview"; week: string }
  | { type: "quizpass"; quiz: string; score: number }
  | { type: "quizprogress"; quiz: string }
  | { type: "quiznotstarted"; quiz: string };

interface Report {
  batch: { batchId: string; name: string; status: string; capacity: number; courseId: string; idphRequired: boolean };
  tiles: {
    enrollment: { enrolled: number; capacity: number; byStatus: Record<string, number> };
    money: { expected: number; collected: number; outstanding: number; owingCount: number };
    quiz?: { average: number | null; gradedCount: number };
    homework?: { submitted: number; overdue: number; awaitingReview: number };
    idph?: { Draft: number; Authorized: number; "Pending Exam": number };
    exam?: { passed: number; failed: number; pending: number };
  };
  attention: { studentId: string; fullName: string; status: string; flagCount: number; flags: Flag[] }[];
}

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

function enrollmentSub(byStatus: Record<string, number>): string {
  const order = ["In Progress", "Graduated", "Withdrawn", "Enrolled"];
  const parts = order.filter((s) => (byStatus[s] ?? 0) > 0).map((s) => `${byStatus[s]} ${s}`);
  return parts.join(" · ") || "No students yet";
}

/** The money column: one pill, always present - either what they owe, or settled. */
function moneyChip(flags: Flag[]) {
  const owes = flags.find((f) => f.type === "owes");
  if (owes && owes.type === "owes")
    return <span className="fchip fc-owes">{owes.amount > 0 ? `Owes ${money(owes.amount)}` : "Owes balance"}</span>;
  if (flags.some((f) => f.type === "paid")) return <span className="fchip fc-paid">Paid in full</span>;
  return <span className="fchip fc-none">-</span>;
}

/**
 * The coursework column: problem pills (failing quiz / missing homework), plus a
 * "HW to review" pill when they've uploaded work you haven't acknowledged.
 * "On track" only when there's nothing at all to show.
 */
function courseChips(flags: Flag[]) {
  const chips = flags
    .map((f, i) =>
      f.type === "fail" ? (
        <span key={i} className="fchip fc-fail">Failing quiz · {f.quiz} {f.score}%</span>
      ) : f.type === "homework" ? (
        <span key={i} className="fchip fc-hw">Missing homework · {f.week}</span>
      ) : f.type === "toreview" ? (
        <span key={i} className="fchip fc-review">HW to review · {f.week}</span>
      ) : f.type === "quizpass" ? (
        <span key={i} className="fchip fc-quizpass">Quiz submitted · {f.quiz} {f.score}%</span>
      ) : f.type === "quizprogress" ? (
        <span key={i} className="fchip fc-quizprogress">Quiz in progress · {f.quiz}</span>
      ) : f.type === "quiznotstarted" ? (
        <span key={i} className="fchip fc-quizwait">Quiz not started · {f.quiz}</span>
      ) : null
    )
    .filter(Boolean);
  return chips.length ? chips : <span className="fchip fc-ok">On track</span>;
}

export default function BatchReportPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [rep, setRep] = useState<Report | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    api<Report>(`/admin/batches/${id}/report`)
      .then((d) => live && setRep(d))
      .catch((e) => live && setError(e instanceof AdminError ? e.message : "Could not load report"));
    return () => {
      live = false;
    };
  }, [id]);

  const back = <p className="subhead"><Link to={`/admin/batches/${id}`}>← Back to batch</Link></p>;

  if (error) return <>{back}<div className="login-err">{error}</div></>;
  if (!rep) return <>{back}<p className="subhead">Loading report…</p></>;

  const t = rep.tiles;
  const needsAttention = rep.attention.filter((s) => s.flagCount > 0).length;
  const toReview = rep.attention.filter((s) => s.flags.some((f) => f.type === "toreview")).length;
  const activeRows = rep.attention.filter((s) => s.status !== "Withdrawn");
  const withdrawnRows = rep.attention.filter((s) => s.status === "Withdrawn");

  return (
    <>
      <p className="subhead no-print"><Link to={`/admin/batches/${rep.batch.batchId}`}>← Back to batch</Link></p>

      <div className="row" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ fontSize: 22 }}>{rep.batch.name} - Report</h1>
          <span className={badgeClass(rep.batch.status)}>{rep.batch.status}</span>
        </div>
        <button className="rbtn no-print" onClick={() => window.print()}>Print</button>
      </div>

      <div className="card pad rep-card">
        <div className="rep-grid">
          <div className="paystat">
            <div className="lbl">Enrollment</div>
            <div className="val">{t.enrollment.enrolled} <span className="rep-of">/ {t.enrollment.capacity}</span></div>
            <div className="rep-sub">{enrollmentSub(t.enrollment.byStatus)}</div>
          </div>

          <div className="paystat paid">
            <div className="lbl">Money</div>
            <div className="val">{money(t.money.collected)}</div>
            <div className="rep-sub">
              of {money(t.money.expected)}
              {t.money.outstanding > 0 && <> · <span className="rep-due">{money(t.money.outstanding)} due</span></>}
            </div>
          </div>

          {t.quiz && (
            <div className="paystat">
              <div className="lbl">Quiz average</div>
              {t.quiz.gradedCount > 0 ? (
                <>
                  <div className="val">{t.quiz.average}%</div>
                  <div className="rep-sub">{t.quiz.gradedCount} graded</div>
                </>
              ) : (
                <>
                  <div className="val rep-muted">-</div>
                  <div className="rep-sub">No quizzes yet</div>
                </>
              )}
            </div>
          )}

          {t.homework && (
            <div className="paystat">
              <div className="lbl">Homework</div>
              <div className="val">{t.homework.submitted} <span className="rep-of">in</span></div>
              <div className="rep-sub">
                {t.homework.overdue > 0 ? <span className="rep-warn">{t.homework.overdue} overdue</span> : "none overdue"}
                {t.homework.awaitingReview > 0 && (
                  <> · <span className="rep-review">{t.homework.awaitingReview} to review</span></>
                )}
              </div>
            </div>
          )}

          {t.idph && (
            <div className="paystat rep-info">
              <div className="lbl">IDPH status</div>
              <div className="val">{t.idph.Authorized} <span className="rep-of">authorized</span></div>
              <div className="rep-sub">{t.idph.Draft} in Draft{t.idph["Pending Exam"] > 0 ? ` · ${t.idph["Pending Exam"]} Pending Exam` : ""}</div>
            </div>
          )}

          {t.exam && (
            <div className="paystat">
              <div className="lbl">Exam status</div>
              <div className="val">{t.exam.passed} <span className="rep-of">passed</span></div>
              <div className="rep-sub">
                {t.exam.failed > 0 && <span className="rep-due">{t.exam.failed} Failed</span>}
                {t.exam.failed > 0 && " · "}{t.exam.pending} pending
              </div>
            </div>
          )}
        </div>

        <div className="attn-head">
          Students
          <span className="attn-count">{rep.attention.length}</span>
          {needsAttention > 0 && <span className="attn-warn">{needsAttention} need attention</span>}
          {toReview > 0 && <span className="attn-review">{toReview} to review</span>}
        </div>

        {rep.attention.length === 0 ? (
          <p className="sub" style={{ marginTop: 0 }}>No students enrolled in this batch yet.</p>
        ) : (
          <div className="attn-list">
            <div className="attn-row attn-hrow" aria-hidden>
              <span>Student</span><span>Payment</span><span>Coursework</span><span />
            </div>
            {/* Group labels only when the roster is actually split. The server
                already sorts active-first, so a single pass keeps that order. */}
            {withdrawnRows.length > 0 && (
              <div className="attn-group attn-group-first">Active · {activeRows.length}</div>
            )}
            {activeRows.map((s) => (
              <div key={s.studentId} className="attn-row" onClick={() => nav(`/admin/students/${s.studentId}`)}>
                <span className="attn-name">{s.fullName}</span>
                <span className="attn-money">{moneyChip(s.flags)}</span>
                <span className="attn-chips">{courseChips(s.flags)}</span>
                <span className="attn-arrow" aria-hidden>›</span>
              </div>
            ))}
            {withdrawnRows.length > 0 && (
              <>
                <div className="attn-group">Withdrawn · {withdrawnRows.length}</div>
                {withdrawnRows.map((s) => (
                  <div key={s.studentId} className="attn-row attn-row-out" onClick={() => nav(`/admin/students/${s.studentId}`)}>
                    <span className="attn-name">{s.fullName}</span>
                    <span className="attn-money">{moneyChip(s.flags)}</span>
                    <span className="attn-chips">{courseChips(s.flags)}</span>
                    <span className="attn-arrow" aria-hidden>›</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
